import React, { useState, useEffect } from "react";
import SEO from "../seo";
import { STAFF_ACCOUNTS } from "../constant_data";
const ManageYourXpageAccount = () => {
  const [activeSection, setActiveSection] = useState("log-in");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "log-in",
        "account-information",
        "staff-accounts",
        "subscription-plan",
        "billing",
        "domain",
        "security",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Manage Your Xpage Account – Help Center"
        description="Learn how to log in, manage account information, staff accounts, subscriptions, billing, domains, and security on xPage."
        canonical="https://help.xpage.ai/help/landing-page-account/Manage%20Your%20Xpage%20Account"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Manage Your Xpage Account
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Your xPage account is the control hub for your landing page. From
          logging in to managing staff, subscriptions, billing, and security
          settings, everything is accessible from your dashboard.
        </p>

        {/* Login Section */}
        <section id="log-in" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Log in to Your Xpage Account
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Access your dashboard with these simple steps:
            </p>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Go to <strong>xPage.ai</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Log In</strong> (top right)
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Enter your registered email address and password
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Sign In</strong> to access your dashboard
                </span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Forgot your
                password? Click Forgot Password? on the login page to reset it
                instantly.
              </p>
            </div>
          </div>
        </section>

        {/* Account Information Section */}
        <section id="account-information" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Update Account Information
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Navigate to <strong>Dashboard → Settings</strong> to manage your
              account details:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                <strong>Landing Page Information:</strong> Edit your landing
                page name, business email, phone number, and physical address
              </li>
              <li>
                <strong>Time Zone & Currency:</strong> Set your preferred time
                zone and default currency for orders
              </li>
              <li>
                <strong>Weight Unit:</strong> Default is kg, can be changed if
                needed
              </li>
            </ul>
          </div>
        </section>

        {/* Staff Accounts Section */}
        <section id="staff-accounts" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Staff Accounts
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Staff limits by plan:
            </h3>

            <div className="flex flex-wrap gap-4 mb-3">
              <div className="bg-system50 rounded-lg p-3">
                <div className="text-sm font-semibold text-systemTitle">
                  Starter
                </div>
                <div className="text-sm text-system600">
                  {STAFF_ACCOUNTS.STARTER}staff accounts
                </div>
              </div>
              <div className="bg-system50 rounded-lg p-3">
                <div className="text-sm font-semibold text-systemTitle">
                  Growth
                </div>
                <div className="text-sm text-system600">
                  {STAFF_ACCOUNTS.GROWTH} staff accounts
                </div>
              </div>
              <div className="bg-system50 rounded-lg p-3">
                <div className="text-sm font-semibold text-systemTitle">
                  Pro
                </div>
                <div className="text-sm text-system600">
                  {STAFF_ACCOUNTS.PRO} staff accounts
                </div>
              </div>
              <div className="bg-system50 rounded-lg p-3">
                <div className="text-sm font-semibold text-systemTitle">
                  Enterprise
                </div>
                <div className="text-sm text-system600">
                  {STAFF_ACCOUNTS.ENTERPRISE}staff accounts
                </div>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps to add or remove staff:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Go to <strong>Settings → Staff Accounts</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Add Staff</strong> and assign roles
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Remove staff when no longer needed to maintain security
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Subscription Plan Section */}
        <section id="subscription-plan" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Change Your Subscription Plan
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Go to <strong>Settings → Billing & Subscription</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select your desired plan:{" "}
                  <strong>Starter, xPage, Pro, or Enterprise</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Upgrades apply instantly, with prorated charges. Downgrades
                  take effect at the next billing cycle.
                </span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Higher-tier plans
                offer lower transaction fees and more staff accounts.
              </p>
            </div>
          </div>
        </section>

        {/* Billing Section */}
        <section id="billing" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            View and Manage Billing
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Access <strong>Billing & Subscription</strong> to:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                See your current plan, billing history, and upcoming charges
              </li>
              <li>Download invoices for recordkeeping</li>
              <li>Update your payment method securely anytime</li>
              <li>
                <strong>Threshold Billing:</strong> xPage charges transaction
                fees once your fees reach the current limit (starting at $15,
                increasing with on-time payments)
              </li>
            </ul>
          </div>
        </section>

        {/* Domain Section */}
        <section id="domain" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Connect and Manage Your Domain
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Go to <strong>Settings → Domain</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Connect a custom domain you own or purchase one through xPage
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  A free SSL certificate is automatically included for secure
                  HTTPS browsing
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Security Section */}
        <section id="security" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Keep Your Account Secure
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Use a <strong>strong password</strong> (minimum 8 characters,
                mix of letters, numbers, and symbols)
              </li>
              <li>
                <strong>Change your password regularly</strong> (Settings →
                Security)
              </li>
              <li>
                <strong>Enable two-factor authentication (2FA)</strong> if
                available
              </li>
              <li>
                <strong>Remove inactive staff accounts</strong> to prevent
                unauthorized access
              </li>
            </ul>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Log in regularly to
                monitor orders, performance, and billing. Keep your staff list
                and permissions updated. Review your plan and billing thresholds
                to optimize costs.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#log-in"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "log-in"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Log in to Your Account
              </a>
            </li>
            <li>
              <a
                href="#account-information"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "account-information"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Account Information
              </a>
            </li>
            <li>
              <a
                href="#staff-accounts"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "staff-accounts"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Staff Accounts
              </a>
            </li>
            <li>
              <a
                href="#subscription-plan"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "subscription-plan"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Subscription Plan
              </a>
            </li>
            <li>
              <a
                href="#billing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "billing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Billing
              </a>
            </li>
            <li>
              <a
                href="#domain"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "domain"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Domain
              </a>
            </li>
            <li>
              <a
                href="#security"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "security"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Security
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManageYourXpageAccount;
